<script setup lang="ts">
import { ref, nextTick, watch, computed } from 'vue';
import { useChat } from './composables/useChat';
import { initializeChatTracking, trackLinkClick, trackConversationDeleted } from './logging/events';
import { deleteConversation } from './logging/server';
import { filterForDisplay } from './utils/messageFilters';
import ChatMessage from './components/ChatMessage.vue';
import ChatInput from './components/ChatInput.vue';
import DisclaimerChatMessage from './components/DisclaimerChatMessage.vue';

const { messages, isStreaming, isFailure, sendMessage } = useChat();

const displayMessages = computed(() => filterForDisplay(messages.value));
const chatInput = ref<InstanceType<typeof ChatInput> | null>(null);
const consentGiven = ref(false);
const hasInteracted = ref(false);
const { workspace, isCustomWorkspace } = window.chatConfig;

const handleSendMessage = (content: string) => {
    hasInteracted.value = true;
    sendMessage(content);
};

const handleDeleteConversation = () => {
    if (hasInteracted.value) {
        deleteConversation();
    }
    trackConversationDeleted(displayMessages.value.length);
    consentGiven.value = false;
    messages.value = [];
};

const reload = () => location.reload();

const scrollToBottom = () => {
    nextTick(() => setTimeout(() => {
        window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
        });
    }, 50));
};

watch([
    () => messages.value.length,
    () => messages.value.map(m => m.content).join(''),
    () => isStreaming,
], scrollToBottom);

watch(consentGiven, (newValue) => {
    if (newValue) {
        scrollToBottom();
    }
});

watch(isStreaming, (newValue, oldValue) => {
    if (oldValue && !newValue) {
        nextTick(() => {
            if (window.matchMedia('(pointer: fine)').matches) {
                chatInput.value?.focus();
            }
        });
    }
});

initializeChatTracking(messages, consentGiven);
</script>

<template>
    <div class="chat__output">
        <div class="container">
            <div class="chat__custom-workspace alert alert-warning" v-if="isCustomWorkspace">
                Sie testen gerade den Workspace <code>{{ workspace }}</code>.
            </div>
            <div
                class="chat__messages"
                role="log"
                v-if="displayMessages.length > 0"
            >
                <h2 class="visually-hidden">Chatverlauf</h2>
                <DisclaimerChatMessage @consent="consentGiven = true" />
                <template v-if="consentGiven">
                    <ChatMessage
                        v-for="(message, index) in displayMessages"
                        :key="message.timestamp.getTime()"
                        :message="message"
                        :allowFeedback="index > 0"
                        @link-click="trackLinkClick"
                    />
                </template>
            </div>

            <div v-else class="chat__alert">
                <h2 class="h3">Chat erfolgreich gelöscht</h2>
                <p>Dein Chat-Verlauf wurde gelöscht. Du kannst die Seite jetzt schließen oder einen neuen Chat starten.</p>
                <button class="btn btn-primary" @click="reload">Neuen Chat starten</button>
            </div>
        </div>
    </div>

    <Transition name="chat-interface">
        <ChatInput v-if="consentGiven" ref="chatInput" :isStreaming="isStreaming" :isFailure="isFailure" @send-message="handleSendMessage" @delete-conversation="handleDeleteConversation" />
    </Transition>
</template>

